import React, { useState, useEffect, useRef } from 'react';
import {
  PlusCircle, Calculator, CheckCircle, Settings, 
  Package, DollarSign, HelpCircle, Route, 
  MapPin, Droplet, Percent, CreditCard, AlertTriangle,
  Truck, Globe, Coins, History, Eye, EyeOff, BarChart3, FileText, Download
} from 'lucide-react';
import {
  VehicleType, TrailerType, CargoType, QuotationData,
  Currency, MapLocation, RouteInfo, TollDetails, FuelUnit
} from '../types';
import { calculateDetailedTransportCost, calculateMultiSegmentTransportCost } from '../utils/calculationUtils';
import { getTollCosts } from '../services/tollService';
import QuotationFormLocationSection from './QuotationFormLocationSection';
import { supabase } from '../supabase/client';
import VehicleCatalogDisplay from './VehicleCatalogDisplay';
import CurrencySelector from './CurrencySelector';
import TokenConfirmationModal from './TokenConfirmationModal';
import TokenLimitModal from './TokenLimitModal';
import LoadingOverlay from './LoadingOverlay';
import TollDetailsPanel from './TollDetailsPanel';
import MapImageCapture, { MapImageCaptureRef } from './MapImageCapture';
import { convertCurrency, getCurrencySymbol } from '../utils/currencyUtils';
import { generateQuotationPDF } from '../utils/pdfUtils';
import { downloadImage, dataURLToBlob, downloadBlob } from '../utils/imageUtils';
import { generateStaticMapUrl } from '../utils/mapUtils';
import { calculateCostWithTax } from '../utils/calculationUtils';
import { useTokenManagement } from '../services/tokenService';
import { analyticsService } from '../services/analyticsService';
import useTranslate from '../hooks/useTranslate';
import { useLanguage } from '../contexts/LanguageContext';
import { getCargoRiskDetails, CARGO_RISK_DETAILS_ES } from '../utils/cargoDetailsUtils';
import { CARGO_RISK_FACTORS } from '../types';

interface QuotationFormProps {
  userId: string;
  showAdvancedMode: boolean;
  setShowAdvancedMode: (show: boolean) => void;
  onSaveQuotation: () => void;
}

const QuotationForm: React.FC<QuotationFormProps> = ({ 
  userId, 
  showAdvancedMode, 
  setShowAdvancedMode, 
  onSaveQuotation 
}) => {
  // Core form states
  const [vehicleType, setVehicleType] = useState<VehicleType | ''>('');
  const [trailerType, setTrailerType] = useState<TrailerType | null>(null);
  const [cargoType, setCargoType] = useState<CargoType>(CargoType.GENERAL_MERCHANDISE);
  const [fuelPrice, setFuelPrice] = useState<number | null>(1.5);
  const [fuelUnit, setFuelUnit] = useState<FuelUnit>(FuelUnit.LITER);
  const [profitMargin, setProfitMargin] = useState<number>(0);
  const [taxPercentage, setTaxPercentage] = useState<number>(16);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(Currency.USD);

  // Additional destinations visibility state
  const [showAdditionalDestinations, setShowAdditionalDestinations] = useState<boolean>(false);

  // Location states
  const [origin, setOrigin] = useState<{ address: string; location: MapLocation | null }>({
    address: '',
    location: null
  });
  const [destination, setDestination] = useState<{ address: string; location: MapLocation | null }>({
    address: '',
    location: null
  });
  const [additionalDestinations, setAdditionalDestinations] = useState<{ 
    address: string; 
    location: MapLocation | null;
    loadStatus?: 'loaded' | 'empty';
  }[]>([]);

  // Load status for the first segment (origin to main destination)
  const [firstSegmentLoadStatus, setFirstSegmentLoadStatus] = useState<'loaded' | 'empty'>('loaded');

  // Route calculation states
  const [routeInfo, setRouteInfo] = useState<RouteInfo | null>(null);
  const [calculatingRoute, setCalculatingRoute] = useState<boolean>(false);
  const [calculatedRoute, setCalculatedRoute] = useState<boolean>(false);

  // Quotation result states
  const [quotationResult, setQuotationResult] = useState<QuotationData | null>(null);
  const [tollDetails, setTollDetails] = useState<TollDetails | null>(null);
  const [loadingQuotation, setLoadingQuotation] = useState<boolean>(false);
  const [showTokenConfirmation, setShowTokenConfirmation] = useState<boolean>(false);
  const [showTokenLimit, setShowTokenLimit] = useState<boolean>(false);
  const [tollCalculationError, setTollCalculationError] = useState<boolean>(false);

  // Get language utilities
  const { t, language } = useLanguage();
  const { translateVehicleType, translateTrailerType, translateCargoType } = useTranslate();

  // Map reference for resetting
  const mapRef = useRef<any>(null);

  // Reference to the results section for auto-scrolling
  const resultsRef = useRef<HTMLDivElement>(null);

  // Use token management hook
  const { tokens, hasTokens, loading: tokenLoading, isAdmin, useToken, reloadTokens } = useTokenManagement(userId);

  // Track form interactions
  useEffect(() => {
    analyticsService.trackPageView('quotation_form');
  }, []);

  // Auto-scroll to results when quotation generation completes
  useEffect(() => {
    // Only scroll if we just finished loading and have results
    if (!loadingQuotation && quotationResult && resultsRef.current) {
      console.log('Quotation generation completed, scrolling to results...');
      
      // Add a small delay to ensure the DOM has fully updated
      setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 300);
    }
  }, [loadingQuotation, quotationResult]);

  // Reset trailer type when vehicle type changes
  useEffect(() => {
    const vehiclesWithoutTrailer = [
      VehicleType.PICKUP_TRUCK,
      VehicleType.DUAL_WHEEL_PICKUP,
      VehicleType.RABON,
      VehicleType.TORTON
    ];

    if (vehicleType && vehiclesWithoutTrailer.includes(vehicleType)) {
      setTrailerType(null);
    }
  }, [vehicleType]);

  const handleRouteCalculated = (info: RouteInfo) => {
    console.log("Route calculated in QuotationForm:", info);
    setRouteInfo(info);
    setCalculatedRoute(info.distance > 0);
    
    // Always update origin and destination with validated coordinates from map
    if (info.origin) {
      setOrigin({
        address: info.originAddress || origin.address,
        location: info.origin
      });
    }
    
    if (info.destination) {
      setDestination({
        address: info.destinationAddress || destination.address,
        location: info.destination
      });
    }
  };

  const canGenerateQuotation = (): boolean => {
    if (!vehicleType || !origin.location || !destination.location || !routeInfo) {
      return false;
    }

    if (showAdditionalDestinations && additionalDestinations.length > 0) {
      return additionalDestinations.every(dest => dest.location !== null);
    }

    return routeInfo.distance > 0;
  };

  const getMissingRequirementsMessage = (): string => {
    const missingItems: string[] = [];
    
    if (!vehicleType) {
      missingItems.push(t('quotationForm.helpers.missingVehicleType'));
    }
    
    if (!origin.location) {
      missingItems.push(t('quotationForm.helpers.missingOrigin'));
    }
    
    if (!destination.location) {
      missingItems.push(t('quotationForm.helpers.missingDestination'));
    }
    
    if (!routeInfo || routeInfo.distance <= 0) {
      missingItems.push(t('quotationForm.helpers.routeNotCalculated'));
    }
    
    if (showAdditionalDestinations && additionalDestinations.length > 0) {
      const missingAdditionalLocations = additionalDestinations.some(dest => !dest.location);
      if (missingAdditionalLocations) {
        missingItems.push(t('quotationForm.helpers.missingAdditionalDestinationLocation'));
      }
    }
    
    if (missingItems.length === 0) {
      return '';
    }
    
    return t('quotationForm.helpers.missingInfoDetails', { details: missingItems.join(', ') });
  };
  const handleGenerateQuotation = async () => {
    console.log('Generate quotation button clicked');
    
    if (!canGenerateQuotation()) {
      console.warn('Cannot generate quotation: missing required data');
      return;
    }

    if (!hasTokens && !isAdmin) {
      console.log('User has no tokens, showing token limit modal');
      setShowTokenLimit(true);
      return;
    }

    console.log('Showing token confirmation modal');
    setShowTokenConfirmation(true);
  };

  const handleConfirmTokenUse = async () => {
    setShowTokenConfirmation(false);
    
    if (!hasTokens && !isAdmin) {
      console.log('User has no tokens after confirmation');
      setShowTokenLimit(true);
      return;
    }

    console.log('Token confirmed, proceeding with quotation generation');
    setLoadingQuotation(true);
    setTollCalculationError(false);

    try {
      // Use token first
      if (!isAdmin) {
        const tokenUsed = await useToken('Cotización generada');
        if (!tokenUsed) {
          console.error('Failed to use token');
          setShowTokenLimit(true);
          return;
        }
      }

      console.log('Generating quotation with vehicle type:', vehicleType);

      let totalCost = 0;
      let baseCost = 0;
      let costBreakdown: Record<string, number> = {};
      let calculatedTollDetails: TollDetails | null = null;

      // Calculate toll costs
      try {
        console.log('Calculating toll costs...');
        // Extract waypoints from additional destinations that have valid locations
        const waypointsForTolls = additionalDestinations
          .filter(dest => dest.location !== null)
          .map(dest => dest.location!);
        
        console.log('Waypoints for toll calculation:', waypointsForTolls.length);
        
        calculatedTollDetails = await getTollCosts(
          origin.location!,
          destination.location!,
          vehicleType as VehicleType,
          origin.address,
          destination.address,
          waypointsForTolls
        );
        
        console.log('Toll calculation completed:', calculatedTollDetails);
      } catch (tollError) {
        console.error('Error calculating tolls:', tollError);
        setTollCalculationError(true);
        calculatedTollDetails = {
          tollStops: [],
          totalTollCost: 0,
          lastUpdated: new Date()
        };
      }

      // Determinar si es ruta multi-segmento
      const isMultiSegment = showAdditionalDestinations && additionalDestinations.length > 0;

      // UPDATED: Multiply toll cost by 2 if NOT multi-segment route
      let adjustedTollCost = calculatedTollDetails.totalTollCost;
      if (!isMultiSegment) {
        adjustedTollCost = calculatedTollDetails.totalTollCost * 2;
        console.log(`Toll cost doubled for round trip: ${calculatedTollDetails.totalTollCost} x 2 = ${adjustedTollCost}`);
      }

      // Calculate transport costs
      if (isMultiSegment) {
        // Multi-segment route calculation
        const segments = [];
        
        // First segment: origin to main destination
        segments.push({
          distance: routeInfo!.distance,
          loadStatus: firstSegmentLoadStatus
        });
        
        // Additional segments
        if (routeInfo!.segmentDistances && routeInfo!.segmentLoadStatus) {
          for (let i = 0; i < routeInfo!.segmentDistances.length; i++) {
            segments.push({
              distance: routeInfo!.segmentDistances[i],
              loadStatus: (routeInfo!.segmentLoadStatus[i] || 'loaded') as 'loaded' | 'empty'
            });
          }
        }
        
        const costResult = calculateMultiSegmentTransportCost(
          segments,
          vehicleType as VehicleType,
          trailerType,
          fuelPrice || 0,
          fuelUnit
        );
        
        baseCost = costResult.totalCost;
        costBreakdown = costResult.breakdown;
      } else {
        // Single route calculation
        const costResult = calculateDetailedTransportCost(
          routeInfo!.distance,
          vehicleType as VehicleType,
          trailerType,
          fuelPrice || 0,
          firstSegmentLoadStatus === 'loaded',
          fuelUnit
        );
        
        baseCost = costResult.totalCost;
        costBreakdown = costResult.breakdown;
      }

      // Add adjusted toll cost to breakdown
      costBreakdown.tollCost = adjustedTollCost;

      // Recalculate total with tolls
      totalCost = Object.values(costBreakdown).reduce((sum, cost) => sum + cost, 0);

      // Apply profit margin
      const profitAmount = (totalCost * profitMargin) / 100;
      const costWithProfit = totalCost + profitAmount;

      // Apply risk factor
      const riskFactor = CARGO_RISK_FACTORS[cargoType] || 1.0;
      const riskAmount = costWithProfit * (riskFactor - 1);
      const finalCost = costWithProfit + riskAmount;

      // Keep the main destination as the destination field
      // Additional destinations will be stored in waypoints array
      const finalDestination = destination.address;
      const finalDestinationAddress = destination.address;

      // Create quotation object
      const quotationData: QuotationData = {
        userId,
        origin: origin.address,
        destination: finalDestination,
        originAddress: origin.address,
        destinationAddress: finalDestinationAddress,
        distance: routeInfo!.distance,
        fuelPrice,
        fuelPriceUnit: fuelUnit,
        vehicleType: vehicleType as VehicleType,
        trailerType,
        cargoType,
        totalCost: Math.ceil(finalCost),
        baseCost: Math.ceil(totalCost),
        profitMargin,
        profitAmount: Math.ceil(profitAmount),
        riskFactor,
        riskAmount: Math.ceil(riskAmount),
        createdAt: new Date(),
        costBreakdown,
        tollDetails: calculatedTollDetails,
        selectedCurrency,
        isMultiSegmentRoute: isMultiSegment,
        segmentDistances: routeInfo!.segmentDistances,
        segmentDurations: routeInfo!.segmentDurations,
        segmentLoadStatus: routeInfo!.segmentLoadStatus,
        waypoints: additionalDestinations.map(dest => dest.address)
      };

      // Generate static map URL for the route
      if (origin.location && destination.location) {
        const waypoints = additionalDestinations.map(dest => dest.location).filter((loc): loc is MapLocation => !!loc);
        const mapImageUrl = generateStaticMapUrl(
          origin.location,
          destination.location,
          waypoints,
          routeInfo!.encodedPolyline,
          import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
          640,
          320,
          2
        );
        quotationData.mapImageUrl = mapImageUrl;
      }

      setQuotationResult(quotationData);
      setTollDetails(calculatedTollDetails);

      // Auto-save the quotation immediately after generation
      try {
        console.log('Auto-saving quotation to database...');
        
        const quotationToSave = {
          user_id: quotationData.userId,
          origin: quotationData.origin,
          destination: quotationData.destination,
          distance: quotationData.distance,
          fuel_price: quotationData.fuelPrice,
          fuel_price_unit: quotationData.fuelPriceUnit,
          vehicle_type: quotationData.vehicleType,
          trailer_type: quotationData.trailerType,
          total_cost: quotationData.totalCost,
          profit_margin: quotationData.profitMargin,
          profit_amount: quotationData.profitAmount,
          base_cost: quotationData.baseCost,
          cargo_type: quotationData.cargoType,
          risk_factor: quotationData.riskFactor,
          risk_amount: quotationData.riskAmount,
          created_at: new Date().toISOString(),
          map_image_url: quotationData.mapImageUrl,
          origin_address: quotationData.originAddress,
          destination_address: quotationData.destinationAddress,
          cost_breakdown: quotationData.costBreakdown,
          toll_details: quotationData.tollDetails,
          selected_currency: quotationData.selectedCurrency,
          is_multi_segment_route: quotationData.isMultiSegmentRoute,
          segment_distances: quotationData.segmentDistances,
          segment_durations: quotationData.segmentDurations,
          segment_load_status: quotationData.segmentLoadStatus,
          waypoints: quotationData.waypoints,
          captured_map_image: quotationData.capturedMapImage
        };
        
        const { data, error } = await supabase
          .from('quotations')
          .insert(quotationToSave)
          .select()
          .single();
        
        if (error) throw error;
        
        console.log('Quotation auto-saved with ID:', data.id);
        
        // Update the quotation result with the database ID
        setQuotationResult(prev => prev ? { ...prev, id: data.id } : null);
        
        // Track successful save
        analyticsService.trackQuotationSaved(data.id);
        
        // Call the callback to indicate a quotation was saved
        onSaveQuotation();
      } catch (saveError) {
        console.error('Error auto-saving quotation:', saveError);
        // Don't block the user experience if save fails - they can still download PDF
      }

      // Track successful quotation generation
      analyticsService.trackQuotationGenerated({
        distance: quotationData.distance,
        vehicleType: translateVehicleType(quotationData.vehicleType),
        trailerType: quotationData.trailerType ? translateTrailerType(quotationData.trailerType) : undefined,
        cargoType: translateCargoType(quotationData.cargoType),
        totalCost: quotationData.totalCost,
        currency: quotationData.selectedCurrency || Currency.MXN
      });

    } catch (error) {
      console.error('Error generating quotation:', error);
      // Track error in analytics
      analyticsService.trackCustomEvent('quotation_generation_error', {
        error_message: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setLoadingQuotation(false);
    }
  };

  const handleSaveQuotation = async () => {
    if (!quotationResult) return;

    try {
      console.log('Saving quotation to Firestore...');
      
      const quotationData = {
        user_id: quotationResult.userId,
        origin: quotationResult.origin,
        destination: quotationResult.destination,
        distance: quotationResult.distance,
        fuel_price: quotationResult.fuelPrice,
        fuel_price_unit: quotationResult.fuelPriceUnit,
        vehicle_type: quotationResult.vehicleType,
        trailer_type: quotationResult.trailerType,
        total_cost: quotationResult.totalCost,
        profit_margin: quotationResult.profitMargin,
        profit_amount: quotationResult.profitAmount,
        base_cost: quotationResult.baseCost,
        cargo_type: quotationResult.cargoType,
        risk_factor: quotationResult.riskFactor,
        risk_amount: quotationResult.riskAmount,
        created_at: new Date().toISOString(),
        map_image_url: quotationResult.mapImageUrl,
        origin_address: quotationResult.originAddress,
        destination_address: quotationResult.destinationAddress,
        cost_breakdown: quotationResult.costBreakdown,
        toll_details: quotationResult.tollDetails,
        selected_currency: quotationResult.selectedCurrency,
        is_multi_segment_route: quotationResult.isMultiSegmentRoute,
        segment_distances: quotationResult.segmentDistances,
        segment_durations: quotationResult.segmentDurations,
        segment_load_status: quotationResult.segmentLoadStatus,
        waypoints: quotationResult.waypoints,
        captured_map_image: quotationResult.capturedMapImage
      };
      
      const { data, error } = await supabase
        .from('quotations')
        .insert(quotationData)
        .select()
        .single();
      
      if (error) throw error;
      
      console.log('Quotation saved with ID:', data.id);
      
      // Track successful save
      analyticsService.trackQuotationSaved(data.id);
      
      onSaveQuotation();
    } catch (error) {
      console.error('Error saving quotation:', error);
    }
  };

  const handleDownloadPDF = async () => {
    if (!quotationResult) return;

    try {
      // Track PDF download
      analyticsService.trackQuotationDownloaded(quotationResult.id || 'new');
      // Generate PDF with the static map URL
      const imageToUse = quotationResult.mapImageUrl || undefined;
      const doc = await
        generateQuotationPDF(quotationResult, imageToUse, selectedCurrency);
      doc.save(`cotizacion-${Date.now()}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleDownloadMapImage = async () => {
    try {
      if (!quotationResult?.mapImageUrl) {
        throw new Error(t('quotationForm.errors.mapImageMissing'));
      }

      const filename = `mapa-ruta-${Date.now()}.jpg`;

      if (quotationResult?.mapImageUrl) {
        await downloadImage(quotationResult.mapImageUrl, filename);
      }

      // Track map image download
      analyticsService.trackCustomEvent('map_image_downloaded', {
        quotationId: quotationResult?.id || 'new',
        standalone: false
      });
    } catch (error) {
      console.error('Error downloading map image:', error);
    }
  };

  const handleNewQuotationClick = () => {
    console.log('Nueva cotización button clicked - reloading page');
    
    // Track new quotation start before reload
    analyticsService.trackCustomEvent('new_quotation_started');
    
    // Reload the page to ensure complete reset
    window.location.reload();
  };
  const canHaveTrailer = (type: VehicleType | ''): boolean => {
    return type === VehicleType.SINGLE_WITH_2_AXLE_TRAILER || 
           type === VehicleType.SINGLE_WITH_3_AXLE_TRAILER ||
           type === VehicleType.FULL_6_AXLES ||
           type === VehicleType.FULL_7_AXLES ||
           type === VehicleType.FULL_8_AXLES ||
           type === VehicleType.FULL_9_AXLES;
  };

  const formatCurrencyValue = (amount: number): string => {
    const convertedValue = convertCurrency(amount, Currency.MXN, selectedCurrency);
    return `${getCurrencySymbol(selectedCurrency)}${Math.ceil(convertedValue).toLocaleString('es-MX')}`;
  };

  const calculateTotalInCurrency = (): string => {
    if (!quotationResult) return getCurrencySymbol(selectedCurrency) + '0';
    const convertedValue = convertCurrency(quotationResult.totalCost, Currency.MXN, selectedCurrency);
    return `${getCurrencySymbol(selectedCurrency)}${Math.ceil(convertedValue).toLocaleString('es-MX')}`;
  };

  const riskDetails = getCargoRiskDetails(cargoType, language);

  // Component to render fuel price input (always visible)
  const FuelPriceInput = () => (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3 bg-blue-50 p-3 rounded-lg border border-blue-200">
        <h3 className="text-sm font-medium text-blue-800 flex items-center">
          <Droplet size={16} className="mr-1.5 text-blue-600" />
          {t('quotationForm.fuelPrice')}
          <div className="relative inline-block ml-2 group">
            <HelpCircle size={14} className="text-blue-500 cursor-help" />
            <div className="absolute left-0 bottom-full mb-2 w-64 hidden group-hover:block bg-gray-800 text-white text-xs rounded shadow-lg p-2 z-50">
              {t('quotationForm.tooltips.fuelPrice')}
            </div>
          </div>
        </h3>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div>
          <label htmlFor="fuelUnit" className="block text-sm font-medium text-gray-700 mb-1">
            {t('quotationForm.fuelPrice')} - {t('common.unit')}
            <div className="relative inline-block ml-2 group">
              <HelpCircle size={14} className="text-gray-500 cursor-help" />
              <div className="absolute left-0 bottom-full mb-2 w-64 hidden group-hover:block bg-gray-800 text-white text-xs rounded shadow-lg p-2 z-50">
                {t('quotationForm.tooltips.fuelUnit')}
              </div>
            </div>
          </label>
          <div className="flex gap-4 mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="fuelUnit"
                value={FuelUnit.LITER}
                checked={fuelUnit === FuelUnit.LITER}
                onChange={(e) => setFuelUnit(e.target.value as FuelUnit)}
                className="mr-2 text-primary focus:ring-primary"
              />
              <span className="text-sm font-medium text-gray-700">
                {t('common.liter')}
              </span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="fuelUnit"
                value={FuelUnit.GALLON}
                checked={fuelUnit === FuelUnit.GALLON}
                onChange={(e) => setFuelUnit(e.target.value as FuelUnit)}
                className="mr-2 text-primary focus:ring-primary"
              />
              <span className="text-sm font-medium text-gray-700">
                {t('common.gallon')}
              </span>
            </label>
          </div>
        </div>
        <div>
          <label htmlFor="fuelPrice" className="block text-sm font-medium text-gray-700 mb-1">
            {t('quotationForm.fuelPrice')} ({t('common.per')} {fuelUnit === FuelUnit.LITER ? t('common.liter') : t('common.gallon')})
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Droplet size={16} className="text-gray-400" />
            </div>
            <input
              type="number"
              id="fuelPrice"
              min=".1"
              max="50"
              step="0.1"
              value={fuelPrice !== null ? fuelPrice : ''}
              onChange={(e) => {
                const rawValue = e.target.value;
                if (rawValue === '') {
                  setFuelPrice(null);
                } else {
                  const numValue = parseFloat(rawValue);
                  if (isNaN(numValue)) {
                    setFuelPrice(0);
                  } else {
                    setFuelPrice(numValue);
                  }
                }
              }}
              className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="1.5"
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">{t('quotationForm.title')}</h2>
        <p className="text-gray-600">{t('quotationForm.subtitle')}</p>
      </div>

      <div className="p-4 sm:p-6">
        {/* Vehicle Configuration Section */}
        <div className="mb-6">
          <div className="mb-3 p-4 bg-gradient-to-r from-primary-light to-primary-dark rounded-lg text-white shadow-lg">
            <h3 className="text-xl sm:text-2xl font-bold flex items-center">
              <Truck size={24} className="mr-3 text-white" />
              {t('quotationForm.vehicleConfig')}
              <div className="relative inline-block ml-2 group">
                <HelpCircle size={18} className="text-white/80 cursor-help" />
                <div className="absolute left-0 bottom-full mb-2 w-64 hidden group-hover:block bg-gray-800 text-white text-xs rounded shadow-lg p-2 z-50">
                  {t('quotationForm.tooltips.vehicleConfig')}
                </div>
              </div>
            </h3>
          </div>

          <VehicleCatalogDisplay
            selectedVehicleType={vehicleType}
            selectedTrailerType={trailerType}
            onVehicleSelect={setVehicleType}
            onTrailerSelect={setTrailerType}
          />
        </div>

        {/* Always visible Fuel Price Section */}
        <FuelPriceInput />

        {/* Currency Selection - Always visible */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3 bg-gray-50 p-3 rounded-lg border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 flex items-center">
              <Globe size={16} className="mr-1.5 text-primary" />
              {t('quotationForm.result.currency')}
              <div className="relative inline-block ml-2 group">
                <HelpCircle size={14} className="text-gray-500 cursor-help" />
                <div className="absolute left-0 bottom-full mb-2 w-64 hidden group-hover:block bg-gray-800 text-white text-xs rounded shadow-lg p-2 z-50">
                  {t('quotationForm.tooltips.currency')}
                </div>
              </div>
            </h3>
          </div>
          
          <CurrencySelector
            selectedCurrency={selectedCurrency}
            onCurrencyChange={setSelectedCurrency}
          />
        </div>

        {/* Advanced Mode Sections - Cargo Type and Cost Parameters */}
        {showAdvancedMode && (
          <>
            {/* Cargo Type Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3 bg-gray-50 p-3 rounded-lg border border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 flex items-center">
                  <Package size={16} className="mr-1.5 text-primary" />
                  {t('quotationForm.cargoType')}
                  <div className="relative inline-block ml-2 group">
                    <HelpCircle size={14} className="text-gray-500 cursor-help" />
                    <div className="absolute left-0 bottom-full mb-2 w-64 hidden group-hover:block bg-gray-800 text-white text-xs rounded shadow-lg p-2 z-50">
                      {t('quotationForm.tooltips.cargoType')}
                    </div>
                  </div>
                </h3>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label htmlFor="cargoType" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('quotationForm.selectCargoType')}
                  </label>
                  <select
                    id="cargoType"
                    value={cargoType}
                    onChange={(e) => setCargoType(e.target.value as CargoType)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {Object.values(CargoType).map((type) => (
                      <option key={type} value={type}>
                        {translateCargoType(type)}
                      </option>
                    ))}
                  </select>
                  
                  {riskDetails && (
                    <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                      <p className="text-xs text-amber-800">
                        <strong>{t('quotationForm.riskFactor')}</strong> {riskDetails.riskFactor.toFixed(2)}x
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Cost Parameters Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3 bg-gray-50 p-3 rounded-lg border border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 flex items-center">
                  <DollarSign size={16} className="mr-1.5 text-primary" />
                  {t('quotationForm.costParams')}
                  <div className="relative inline-block ml-2 group">
                    <HelpCircle size={14} className="text-gray-500 cursor-help" />
                    <div className="absolute left-0 bottom-full mb-2 w-64 hidden group-hover:block bg-gray-800 text-white text-xs rounded shadow-lg p-2 z-50">
                      {t('quotationForm.tooltips.costParams')}
                    </div>
                  </div>
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="profitMargin" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('quotationForm.profitMargin')} (%)
                    <div className="relative inline-block ml-2 group">
                      <HelpCircle size={14} className="text-gray-500 cursor-help" />
                      <div className="absolute left-0 bottom-full mb-2 w-64 hidden group-hover:block bg-gray-800 text-white text-xs rounded shadow-lg p-2 z-50">
                        {t('quotationForm.tooltips.profitMargin')}
                      </div>
                    </div>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Percent size={16} className="text-gray-400" />
                    </div>
                    <input
                      type="number"
                      id="profitMargin"
                      min="0"
                      max="50"
                      value={profitMargin}
                      onChange={(e) => setProfitMargin(parseFloat(e.target.value) || 0)}
                      className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="0"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('quotationForm.defaultValue', { value: '0%' })}
                  </p>
                </div>

                <div>
                  <label htmlFor="taxPercentage" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('quotationForm.taxVat')} (%)
                    <div className="relative inline-block ml-2 group">
                      <HelpCircle size={14} className="text-gray-500 cursor-help" />
                      <div className="absolute left-0 bottom-full mb-2 w-64 hidden group-hover:block bg-gray-800 text-white text-xs rounded shadow-lg p-2 z-50">
                        {t('quotationForm.tooltips.taxVat')}
                      </div>
                    </div>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CreditCard size={16} className="text-gray-400" />
                    </div>
                    <select
                      id="taxPercentage"
                      min="0"
                      max="50"
                      value={taxPercentage}
                      onChange={(e) => setTaxPercentage(parseFloat(e.target.value))}
                      className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary appearance-none bg-white"
                    >
                      <option value={0}>0% - Sin IVA</option>
                      <option value={8}>8%  </option>
                      <option value={16}>16 </option>
                      <option value={20}>20% </option>
                      <option value={25}>25% </option>
                      <option value={30}>30% </option>
                      <option value={35}>35% </option>
                      <option value={40}>40% </option>
                      <option value={45}>45% </option>
                      <option value={50}>50% </option>
                    </select>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('quotationForm.defaultValue', { value: '0%' })}
                  </p>
                </div>
              </div>
            </div>

          </>
        )}

        {/* Location Section */}
        <QuotationFormLocationSection
          ref={mapRef}
          origin={origin}
          destination={destination}
          additionalDestinations={additionalDestinations}
          setOrigin={setOrigin}
          setDestination={setDestination}
          setAdditionalDestinations={setAdditionalDestinations}
          onRouteCalculated={handleRouteCalculated}
          calculatingRoute={calculatingRoute}
          calculatedRoute={calculatedRoute}
          routeInfo={routeInfo}
          firstSegmentLoadStatus={firstSegmentLoadStatus}
          setFirstSegmentLoadStatus={showAdditionalDestinations ? setFirstSegmentLoadStatus : undefined}
          showAdditionalDestinations={showAdditionalDestinations}
          setShowAdditionalDestinations={setShowAdditionalDestinations}
        />

        {/* Generate Quotation Button */}
        <div className="mb-6 text-center relative">
          <div className="group relative inline-block">
          <button
            onClick={handleGenerateQuotation}
            disabled={!canGenerateQuotation() || loadingQuotation}
            className={`px-8 py-4 rounded-lg font-semibold text-lg shadow-lg transition-all duration-300 flex items-center justify-center mx-auto ${
              canGenerateQuotation() && !loadingQuotation
                ? 'bg-green-600 hover:bg-green-700 text-white hover:shadow-xl transform hover:scale-105'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {loadingQuotation ? (
              <>
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-white mr-3"></div>
                {t('quotationForm.calculating')}
              </>
            ) : (
              <>
                <Calculator size={24} className="mr-3" />
                {t('quotationForm.generateQuotation')}
              </>
            )}
          </button>
            
            {/* Tooltip for disabled button */}
            {!canGenerateQuotation() && !loadingQuotation && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-4 py-2 bg-gray-800 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-50 whitespace-nowrap">
                <div className="text-center">
                  {getMissingRequirementsMessage()}
                </div>
                {/* Arrow pointing down */}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
              </div>
            )}
          </div>
        </div>

        {/* Results Section */}
        {quotationResult && (
          <div ref={resultsRef} className="border-t border-gray-200 pt-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-800 flex items-center">
                <CheckCircle className="mr-2 text-green-500" size={24} />
                {t('quotationForm.result.title')}
              </h3>
              
              <div className="flex items-center space-x-2">
                <CurrencySelector
                  selectedCurrency={selectedCurrency}
                  onCurrencyChange={setSelectedCurrency}
                  className="text-sm"
                />
              </div>
            </div>

            {/* Alert for toll calculation errors */}
            {tollCalculationError && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="text-amber-800 font-medium mb-1">
                      {t('quotationForm.errors.tollApiError')}
                    </h4>
                    <p className="text-amber-700 text-sm">
                      {t('quotationForm.errors.costsMayNotIncludeTolls')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Cost Summary */}
            <div className="bg-gradient-to-r from-primary to-primary-dark text-white p-6 rounded-lg mb-6">
              <div className="text-center">
                <h4 className="text-lg font-semibold mb-2">{t('quotationForm.result.totalCost')}</h4>
                <div className="text-3xl font-bold">
                  {calculateTotalInCurrency()}
                </div>
              </div>
            </div>

            {/* Trip Details */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-3">{t('quotationForm.result.tripDetails')}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('quotationForm.result.origin')}</span>
                    <span className="font-medium text-right">{quotationResult.originAddress}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('quotationForm.result.destination')}</span>
                    <span className="font-medium text-right">{quotationResult.destinationAddress}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('quotationForm.result.oneWayDistance')}</span>
                    <span className="font-medium">{quotationResult.distance.toFixed(2)} km</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('quotationForm.result.roundtripDistance')}</span>
                    <span className="font-medium">{(quotationResult.distance * 2).toFixed(2)} km</span>
                  </div>
              
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-3">{t('quotationForm.result.configuration')}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('quotationForm.result.vehicle')}</span>
                    <span className="font-medium text-right">{translateVehicleType(quotationResult.vehicleType)}</span>
                  </div>
                  {quotationResult.trailerType && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Remolque:</span>
                      <span className="font-medium text-right">{translateTrailerType(quotationResult.trailerType)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('quotationForm.result.merchandise')}</span>
                    <span className="font-medium text-right">{translateCargoType(quotationResult.cargoType)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('quotationForm.result.fuel')}</span>
                    <span className="font-medium">
                      {getCurrencySymbol(Currency.MXN)}{(quotationResult.fuelPrice || 0).toFixed(2)}/{quotationResult.fuelPriceUnit === FuelUnit.GALLON ? t('common.gallon') : t('common.liter')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('quotationForm.result.profit')}</span>
                    <span className="font-medium">{quotationResult.profitMargin}%</span>
                  </div>
                  {quotationResult.riskFactor && quotationResult.riskFactor > 1.0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('quotationForm.result.riskFactor')}</span>
                      <span className="font-medium">{quotationResult.riskFactor.toFixed(2)}x</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('quotationForm.result.currency')}</span>
                    <span className="font-medium">{selectedCurrency}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Cost Breakdown */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
                <BarChart3 className="mr-2" size={18} />
                {t('quotationForm.result.costBreakdown')}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {quotationResult.costBreakdown && Object.entries(quotationResult.costBreakdown).map(([key, value]) => {
                  if (key === 'tollCost' && quotationResult.tollDetails && quotationResult.tollDetails.tollStops.length > 0) {
                    // Render detailed toll breakdown
                    return (
                      <div key={key} className="col-span-1 sm:col-span-2 border-t border-gray-200 pt-2 mt-2">
                        <div className="mb-2">
                          <span className="text-gray-700 font-medium text-sm">{t('quotationForm.costItems.tollDetailsSectionTitle')}</span>
                        </div>
                        {quotationResult.tollDetails.tollStops.map((stop, index) => (
                          <div key={index} className="flex justify-between py-1 pl-4">
                            <span className="text-gray-500 text-xs">{stop.name}</span>
                            <span className="text-gray-700 text-xs">
                              {formatCurrencyValue(calculateCostWithTax(stop.cost, showAdvancedMode ? taxPercentage : 0))}
                            </span>
                          </div>
                        ))}
                        <div className="flex justify-between py-1 pl-4 border-t border-gray-100 mt-1 pt-1">
                          <span className="text-gray-600 font-medium text-sm">{t('quotationForm.costItems.tollCost')}</span>
                          <span className="font-medium text-sm">
                            {formatCurrencyValue(calculateCostWithTax(value, showAdvancedMode ? taxPercentage : 0))}
                          </span>
                        </div>
                        {!quotationResult.isMultiSegmentRoute && (
                          <div className="pl-4 mt-1">
                            <p className="text-xs text-gray-500 italic">
                              El costo de las casetas incluye ida y vuelta
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  } else if (key !== 'tollCost') {
                    // Render other cost items normally
                    return (
                      <div key={key} className="flex justify-between py-1">
                        <span className="text-gray-600">{t(`quotationForm.costItems.${key}`)}</span>
                        <span className="font-medium">{formatCurrencyValue(value)}</span>
                      </div>
                    );
                  }
                  return null;
                })}
                <div className="col-span-1 sm:col-span-2 border-t border-gray-200 pt-2 mt-2">
                  <div className="flex justify-between py-1 font-bold text-primary">
                    <span>{t('quotationForm.costItems.total')}</span>
                    <span>{calculateTotalInCurrency()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={handleNewQuotationClick}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-semibold flex items-center justify-center transition-colors shadow-md hover:shadow-lg"
              >
                <PlusCircle className="mr-2" size={20} />
               {t('dashboard.newQuotation')}
              </button>
              
              <button
                onClick={handleDownloadPDF}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 px-6 rounded-lg font-semibold flex items-center justify-center transition-colors shadow-md hover:shadow-lg"
              >
                <FileText className="mr-2" size={20} />
                {t('quotationForm.result.downloadPDF')}
              </button>
              
              {quotationResult.mapImageUrl && (
                <button
                  onClick={handleDownloadMapImage}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-lg font-semibold flex items-center justify-center transition-colors shadow-md hover:shadow-lg"
                >
                  <Download className="mr-2" size={20} />
                  {t('quotationForm.result.downloadMapImage')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Toll Details Panel */}
        {tollDetails && (
          <div className="mt-6">
            <TollDetailsPanel
              tollDetails={tollDetails}
              vehicleType={quotationResult?.vehicleType}
              taxPercentage={showAdvancedMode ? taxPercentage : 0}
              onTaxPercentageChange={showAdvancedMode ? setTaxPercentage : undefined}
              selectedCurrency={selectedCurrency}
              onCurrencyChange={setSelectedCurrency}
              tollErrorOccurred={tollCalculationError}
              adjustedTotalTollCost={quotationResult?.costBreakdown?.tollCost || 0}
              isMultiSegmentRoute={quotationResult?.isMultiSegmentRoute || false}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      <TokenConfirmationModal
        isOpen={showTokenConfirmation}
        onClose={() => setShowTokenConfirmation(false)}
        onConfirm={handleConfirmTokenUse}
        tokenCount={tokens}
        isLoading={loadingQuotation}
      />

      <TokenLimitModal
        isOpen={showTokenLimit}
        onClose={() => setShowTokenLimit(false)}
        userId={userId}
        onPurchaseComplete={() => {
          setShowTokenLimit(false);
          reloadTokens();
        }}
      />

      {/* Loading Overlay */}
      {loadingQuotation && <LoadingOverlay duration={6000} />}
    </div>
  );
};

export default QuotationForm;